import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './print.css';
import App from './App';
import { AuthProvider } from 'react-oidc-context';
import { HashRouter } from 'react-router-dom';

const {
    REACT_APP_COGNITO_AUTHORITY,
    REACT_APP_COGNITO_CLIENT_ID,
    REACT_APP_COGNITO_REDIRECT_URI,
    REACT_APP_COGNITO_RESPONSE_TYPE,
    REACT_APP_COGNITO_SCOPE,
} = process.env;

const cognitoAuthConfig = {
    authority: REACT_APP_COGNITO_AUTHORITY,
    client_id: REACT_APP_COGNITO_CLIENT_ID,
    redirect_uri: REACT_APP_COGNITO_REDIRECT_URI,
    response_type: REACT_APP_COGNITO_RESPONSE_TYPE ?? 'code',
    scope: REACT_APP_COGNITO_SCOPE ?? 'email openid phone',
};

const requiredEnv = [
    ['REACT_APP_COGNITO_AUTHORITY', cognitoAuthConfig.authority],
    ['REACT_APP_COGNITO_CLIENT_ID', cognitoAuthConfig.client_id],
    ['REACT_APP_COGNITO_REDIRECT_URI', cognitoAuthConfig.redirect_uri],
];

const missingEnv = requiredEnv
    .filter(([, value]) => !value)
    .map(([key]) => key);

if (missingEnv.length) {
    throw new Error(
        `Missing required env variables: ${missingEnv.join(', ')}. `
    );
}

const root = ReactDOM.createRoot(document.getElementById('root'));

// Normalize base URL for HashRouter:
// - Ensure bare origin redirects to '#/' so routes resolve consistently
// - Migrate any pre-hash query (?...) into the hash query to keep a single source of truth
(function normalizeUrlForHashRouter() {
    try {
        const { origin, pathname, search, hash } = window.location;
        // Avoid touching OIDC callback URLs that rely on search params like 'code='
        const isAuthCallback = search && /[?&]code=/.test(search);
        if (isAuthCallback) return;

        let newPathname = pathname.endsWith('/') ? pathname : `${pathname}/`;
        let newHash = hash;
        
        // If there is no hash (or just '#'), default to '#/'
        if (!newHash || newHash === '#') {
            newHash = '#/';
        } else if (!newHash.startsWith('#/')) {
            newHash = `#/${newHash.substring(1)}`;
        }

        // If we already have a pre-hash search, move it after the hash
        if (search) {
            const qs = search.substring(1);
            if (qs) {
                const hasSearchInHash = newHash.includes('?');
                const sep = hasSearchInHash ? '&' : '?';
                newHash = `${newHash}${sep}${qs}`;
            }
        }
        
        const newUrl = `${origin}${newPathname}${newHash}`;
        if (window.location.href !== newUrl) {
            window.history.replaceState({}, document.title, newUrl);
        }
    } catch (e) {
        // No-op on normalization errors
        // console.warn('URL normalization skipped:', e);
    }
})();

root.render(
    <React.StrictMode>
        <AuthProvider {...cognitoAuthConfig}>
            <HashRouter>
                <App />
            </HashRouter>
        </AuthProvider>
    </React.StrictMode>
);
